<template>
  <li v-if="!contextFilter || inContextNodeIds" :id="node.id" :class="['tree-node', {transclusion: isTransclusion}]">
    <!-- Content -->
    <div :class="['content', {highlight: hasMatch}]">
      <div v-if="childCount" :class="arrowClass" @click="toggleCollapsed"></div>
      <div v-else class="drop-area" title="Drop an infobit here to create a child" v-dragula="[]" bag="infobits"></div>
      <infobit :infobit="node.infobit" :tabIndex="tabIndex" ref="infobit">
        <div>
          <button title="View as tree root" class="fa fa-level-up" @click.stop="setSubtreeFilter"></button>
          <button title="Retype"            class="fa fa-exchange" @click="showModal=true"
            :disabled="!loggedInUser"></button>
          <button title="Remove from tree"  class="fa fa-remove"   @click.stop="remove"
            :disabled="!loggedInUser"></button>
        </div>
        <div>
          <button v-for="(sort, sortMode) in sortModes" :title="sort.titleLabel"
            :class="['fa', 'fa-' + sort.icon, {highlight: isSelectedSortMode(sortMode)}]"
            @click.stop="setSortMode(sortMode)" :disabled="!isSortable"></button>
        </div>
        <div>
          <button title="Cut"                  class="fa fa-scissors"  @click.stop="cutToClipboard"
            :disabled="!loggedInUser"></button>
          <button title="Copy"                 class="fa fa-copy"      @click.stop="copyToClipboard"></button>
          <button title="Copy as transclusion" class="fa fa-share-alt" @click.stop="transcludeToClipboard"></button>
          <button title="Paste as child"       class="fa fa-paste"     @click.stop="pasteSubtree"
            :disabled="!clipboardContent || !loggedInUser || isChildNode"></button>
        </div>
      </infobit>
    </div>
    <retype-dialog v-if="showModal" v-model="showModal" :infobit="node.infobit"></retype-dialog>
    <!-- Child nodes -->
    <ul class="child-nodes" v-if="childCount && !isCollapsed" v-dragula="sortedNodes" bag="infobits">
      <tree-node v-for="childNode in sortedNodes" :node="childNode" :key="childNode.id"></tree-node>
    </ul>
  </li>
</template>

<script>
import PasteAction from '../paste-action'

export default {

  name: 'tree-node',

  props: [
    'node'      // the node data (as received from server) underlying this tree-node
  ],

  data () {
    return {
      showModal: false
    }
  },

  computed: {

    isCollapsed () {
      return this.node.collapsed
    },

    isTransclusion () {
      return this.node.isTransclusion
    },

    isChildNode () {
      return this.$store.getters.childNodeIds.includes(this.node.id)
    },

    matchingNodeIds () {
      return this.$store.state.treePanel.matchingNodeIds
    },

    contextFilter () {
      return this.$store.state.treePanel.filter.context
    },

    inContextNodeIds () {
      return this.$store.getters.contextNodeIds.includes(this.node.id)
    },

    childCount () {
      return this.node.nodes.length
    },

    arrowClass () {
      return ['arrow', this.isCollapsed ? 'collapsed' : 'expanded']
    },

    sortModes () {
      return this.$store.state.sortModes
    },

    searchTerm () {
      return this.$store.state.treePanel.filter.searchterm
    },

    clipboardContent () {
      return this.$store.state.clipboard.content
    },

    hasMatch () {
      let terms = this.searchTerm.split(' ')
      for (var i = 0, term; (i < terms.length); i++) {
        term = terms[i]
        if (this.node.infobit.title.toLowerCase().indexOf(term.toLowerCase()) <= -1) {
          return false
        }
      }
      return this.searchTerm && true
    },

    tabIndex () {
      if (this.matchingNodeIds.includes(this.node.id)) {
        if (this.$refs.infobit && this.$refs.infobit.tabIndex === 1) {
          this.$nextTick(function () {
            this.$refs.infobit.$el.focus()
          })
        }
        return this.matchingNodeIds.indexOf(this.node.id) + 1
      }
    },

    sortedNodes () {
      var nodesCopy = this.node.nodes.slice()
      switch (this.node.sort) {
        case 'infobits.sort.title':
          nodesCopy.sort(function (a, b) {
            var infobitTitleA = a.infobit.title.toLowerCase()
            var infobitTitleB = b.infobit.title.toLowerCase()
            if (infobitTitleA < infobitTitleB) {
              return -1
            }
            if (infobitTitleA > infobitTitleB) {
              return 1
            }
            return 0
          })
          break
        case 'infobits.sort.date':
          nodesCopy.sort(function (a, b) {
            return a.infobit.created - b.infobit.created
          })
          break
        case 'infobits.sort.manual':
          nodesCopy = this.node.nodes
          break
        default:
          throw Error(this.node.sort + ' is an action unexpected sort mode')
      }
      return nodesCopy
    },

    isSortable () {
      return this.childCount
    },

    loggedInUser () {
      return this.$store.state.user
    }
  },

  methods: {

    setSubtreeFilter () {
      this.$store.dispatch('setSubtreeFilter', this.node)
    },

    remove () {
      this.$store.dispatch('removeNode', this.node)
    },

    toggleCollapsed () {
      this.$store.dispatch('setCollapsed', {
        node: this.node,
        collapsed: !this.isCollapsed
      })
    },

    copyToClipboard () {
      this.$store.dispatch('copyToClipboard', {content: this.node, action: PasteAction.COPY})
    },

    transcludeToClipboard () {
      this.$store.dispatch('copyToClipboard', {content: this.node, action: PasteAction.TRANSCLUDE})
    },

    cutToClipboard () {
      this.$store.dispatch('copyToClipboard', {content: this.node, action: PasteAction.MOVE})
    },

    pasteSubtree () {
      this.$store.dispatch('pasteSubtree', this.node)
    },

    setSortMode (sortMode) {
      this.$store.dispatch('setSortMode', {
        node: this.node,
        sortMode
      })
    },

    isSelectedSortMode (sortMode) {
      return this.isSortable && this.node.sort === sortMode
    }
  },

  components: {
    'infobit':       require('./infobit.vue'),
    'retype-dialog': require('./retype-dialog.vue')
  }
}
</script>

<style>
.tree-node {
  list-style-type: none;
  margin-top: var(--infobit-list-spacing);
}

.tree-node.transclusion {
  background-color: var(--transclusion-color);
  box-shadow: inset 1px 0px 2px #ccc;
}

.tree-node .content {
  display: flex;
}

.tree-node .content.highlight {
  background-color: var(--search-match-color);
}

.tree-node .content .drop-area {
  flex: 0 0 11px;
  margin-top: 0.4em;
  height: 1.3em;
  border: 1px dotted #c0c0c0;
}

.tree-node .content .arrow {
  flex: 0 0 11px;
  background-repeat: no-repeat;
  background-position: 0px 8px;
  opacity: 0.2;
}

.tree-node .content .arrow:hover {
  opacity: 1;
}

.tree-node .content .arrow.collapsed {
  background-image: url('assets/collapsed.png');
}

.tree-node .content .arrow.expanded {
  background-image: url('assets/expanded.png');
}

.tree-node .content .infobit {
  margin-left: 0.4em;
  padding-top:    var(--infobit-padding-top);
  padding-bottom: var(--infobit-padding-bottom);
  min-width: 0;   /* make word-wrap effective in a flex layout */
}

.tree-node .modal-body ul {
  list-style-type: none;
  padding-left: 0px;
}

.tree-node ul {
  padding-left: 30px;     /* ul browser stylesheet is 40px */
}

.inbox-transit {
  padding-left: 16.6px;   /* 11px + infobit margin-left 0.4em */
}
</style>



// WEBPACK FOOTER //
// tree-node.vue?6c031f86